/*
 * Author: Dw
 * File Created: Monday, 25-06-16 10:49 am
 * Last Modified: Friday, 25-06-20 03:54 pm, by Dw
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProviderName } from './index';
import { PROVIDERS } from './index';
import type { CaptchaStrategy, InstanceMap, ValidateMap } from './strategy';
function isGeetest(inst: InstanceMap[keyof InstanceMap] | null | undefined): inst is InstanceMap['GEETEST'] {
  return inst != null && 'captchaGeeTestObj' in inst;
}
export interface UseCaptchaOpts<P extends ProviderName> {
  strategy?: Extract<CaptchaStrategy, {
    provider: P;
  }> | null;
  onSuccess?: (validate: ValidateMap[P]) => void;
}
export function useCaptcha<P extends ProviderName>({
  strategy,
  onSuccess
}: UseCaptchaOpts<P>) {
  const boxRef = useRef<HTMLDivElement>(null);
  const instRef = useRef<InstanceMap[P] | null>(null);
  const [ready, setReady] = useState(false);
  const successRef = useRef(onSuccess);
  const [verified, setVerified] = useState(false);
  useEffect(() => {
    successRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    let destroyed = false;
    (async () => {
      if (!strategy) {
        return null;
      }
      const provider = PROVIDERS[strategy.provider];
      const inst = (await provider.init(strategy as never // <-- 已由泛型约束，安全断言
      )) as InstanceMap[P];
      if (destroyed) return;
      instRef.current = inst;
      setVerified(false);
      if (isGeetest(inst)) {
        const g = inst.captchaGeeTestObj;
        g.appendTo(boxRef.current!);
        g.onReady(() => {
          setReady(true);
        });
        g.onSuccess(() => {
          const result = g.getValidate();
          if (result) {
            successRef.current?.(result as ValidateMap[P]);
            setVerified(true);
          }
        });
        g.onError(() => {
          setReady(true);
        }); // 宕机/降级时放行
      } else {
        /* 其它 provider（或 none）立即 ready */
        setReady(true);
        setVerified(true);
      }
    })();
    return () => {
      destroyed = true;
      instRef.current?.destroy();
    };
  }, [strategy]);
  const trigger = useCallback(() => instRef.current?.show(), []);
  const reset = useCallback(() => {
    instRef.current?.reset();
    setVerified(false);
  }, []);
  return {
    boxRef,
    ready,
    trigger,
    reset,
    verified
  };
}